import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Loader2, Users, ChevronRight, UserPlus, Trash2, Plus, X } from 'lucide-react'
import type { Season, BracketTemplate, BracketPlayer, GroupMember } from './types'
import { getInitials } from './utils'

interface BracketPlayersViewProps {
  season: Season
  template: BracketTemplate
  players: BracketPlayer[]
  playersLoading: boolean
  // Add player
  showAddPlayer: boolean
  onShowAddPlayer: (show: boolean) => void
  playerSearch: string
  onPlayerSearchChange: (value: string) => void
  availableMembers: GroupMember[]
  membersLoading: boolean
  addingPlayerId: string | null
  onAddPlayer: (userId: string) => void
  // Remove player
  removingPlayerId: string | null
  onRemovePlayer: (userId: string) => void
  // Navigation
  onBackToSeasons: () => void
  onBackToSeason: () => void
  // Animation
  actionsVisible: boolean
}

export function BracketPlayersView({
  season,
  template,
  players,
  playersLoading,
  showAddPlayer,
  onShowAddPlayer,
  playerSearch,
  onPlayerSearchChange,
  availableMembers,
  membersLoading,
  addingPlayerId,
  onAddPlayer,
  removingPlayerId,
  onRemovePlayer,
  onBackToSeasons,
  onBackToSeason,
  actionsVisible,
}: BracketPlayersViewProps) {
  return (
    <Card>
      <CardHeader className="pb-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
          <button onClick={onBackToSeasons} className="hover:text-foreground hover:underline">
            Seasons
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          <button onClick={onBackToSeason} className="hover:text-foreground hover:underline truncate max-w-[120px]">
            {season.name}
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground font-medium truncate">{template.name}</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Players
            </CardTitle>
            <CardDescription>
              {players.length} player{players.length !== 1 ? 's' : ''} registered
            </CardDescription>
          </div>
          <Button 
            size="sm" 
            onClick={() => onShowAddPlayer(true)}
            className={`transition-all duration-300 ease-out ${
              actionsVisible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
            }`}
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Add Player
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Add Player Panel */}
        {showAddPlayer && (
          <div className="mb-4 p-4 rounded-lg border bg-muted/30 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">Add Player to Bracket</span>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { onShowAddPlayer(false); onPlayerSearchChange('') }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Input
              placeholder="Search members..."
              value={playerSearch}
              onChange={e => onPlayerSearchChange(e.target.value)}
            />
            <div className="max-h-48 overflow-y-auto space-y-1">
              {membersLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : availableMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {playerSearch ? 'No matching members found' : 'All group members are already in this bracket'}
                </p>
              ) : (
                availableMembers.map(member => (
                  <div
                    key={member.userId}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                    onClick={() => onAddPlayer(member.userId)}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={member.avatar} alt={member.displayName} />
                        <AvatarFallback className="text-xs">{getInitials(member.displayName)}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-sm">{member.displayName}</span>
                    </div>
                    {addingPlayerId === member.userId ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Players List */}
        {playersLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : players.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-lg">
            <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground mb-2">No players in this bracket yet</p>
            <Button variant="outline" size="sm" onClick={() => onShowAddPlayer(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add First Player
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {players.map((player, index) => (
              <div
                key={player.userId}
                className="flex items-center gap-2 sm:gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors group"
              >
                <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs sm:text-sm font-bold text-primary shrink-0">
                  {index + 1}
                </div>
                <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
                  <AvatarImage src={player.avatar} alt={player.displayName} />
                  <AvatarFallback className="text-sm">{getInitials(player.displayName)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-sm sm:text-base truncate block">{player.displayName}</span>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Joined {new Date(player.joinedAt).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0"
                  disabled={removingPlayerId === player.userId}
                  onClick={() => onRemovePlayer(player.userId)}
                >
                  {removingPlayerId === player.userId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
